import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LegalFooter } from "@/components/LegalFooter";
import { useState } from "react";
import { api } from "@/lib/apiClient";
import { Sparkles, ArrowRight, Loader2, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (!token) {
      toast.error("Reset link is missing or invalid.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset. Sign in with your new password.");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      <div className="hidden md:flex flex-col justify-between bg-zinc-900 text-white p-12 relative overflow-hidden">
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="w-8 h-8 bg-white rounded-md flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-zinc-900" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-xl">NextGen<span className="text-blue-400"> Resolution Experts</span></span>
        </Link>
        <div className="relative">
          <h2 className="font-display font-black tracking-tighter text-5xl leading-[0.95] mb-6">
            Choose a new<br />
            <span className="text-blue-400">password.</span>
          </h2>
          <ul className="space-y-2 text-sm text-zinc-400 mt-6">
            <li>• At least 8 characters</li>
            <li>• Use a mix of letters, numbers &amp; symbols</li>
            <li>• Never reuse passwords across sites</li>
          </ul>
        </div>
        <div className="text-xs text-zinc-500 font-mono relative">This link can only be used once.</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition" data-testid="reset-back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>

          {!token ? (
            <div data-testid="reset-invalid">
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
                <AlertCircle className="w-7 h-7 text-red-600" strokeWidth={2} />
              </div>
              <h1 className="font-display font-black tracking-tighter text-4xl mb-3">Invalid reset link</h1>
              <p className="text-zinc-500 mb-8">
                This URL is missing the reset token. Please request a new reset link.
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white font-bold px-5 py-3 rounded-full hover:bg-zinc-800 transition"
              >
                Request new link <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" strokeWidth={2} />
              </div>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-emerald-600 mb-2">Success</div>
              <h1 className="font-display font-black tracking-tighter text-4xl mb-3">Password updated.</h1>
              <p className="text-zinc-500 mb-8 leading-relaxed">
                You'll be redirected to sign in in a moment.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white font-bold px-5 py-3 rounded-full hover:bg-zinc-800 transition"
              >
                Sign in now <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-2">Reset password</div>
              <h1 className="font-display font-black tracking-tighter text-4xl mb-2">Choose a new one.</h1>
              <p className="text-zinc-500 mb-10">Must be at least 8 characters.</p>

              <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">New password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none transition"
                    data-testid="reset-password"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Confirm password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-type your password"
                    className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none transition"
                    data-testid="reset-confirm"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white font-bold py-4 rounded-full hover:bg-zinc-800 disabled:opacity-50 transition-all hover:-translate-y-0.5"
                  data-testid="reset-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Reset password <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          )}
          <LegalFooter />
        </div>
      </div>
    </div>
  );
}
